/**
 * Phase 45 Plan 45-06 (D-01) — assignee-scoped MyTasks list query hook.
 *
 * Wraps GET /api/tasks/mine (rewritten by Plan 45-03 from membership scope
 * to assigneeId scope).
 *
 * Petra UAT 2026-05-19 — query key now includes the effective user id
 * (impersonated id when active, otherwise own). Without this an admin who
 * impersonates user X sees the admin's MyTasks cached result because the
 * static `['tasks','mine']` key collides across personas. The backend
 * already swaps `currentUser` via the `impersonate` middleware, but the
 * frontend cache layer didn't know about that swap — invalidation in the
 * claim / reassign flows still works because it targets the same key
 * scoped by current persona.
 *
 * Phase 50 Plan 50-06 — extended with tier sort (SORT-01) + tier filter (FC-01).
 * `sortAndFilter` is exported as a pure function so unit tests can import it
 * directly without rendering the hook.
 *
 * Phase 86 Plan 86-04 — `MyTasksPage` now calls this hook exactly ONCE, with
 * no `tiers` option (so `sortAndFilter` only sorts, never filters). Tier
 * filtering happens client-side in the page, alongside the customer/project/
 * search filters. Before this plan the page called the hook TWICE — once
 * with the active tier list, once without, under the mistaken belief that
 * both hit the same TanStack Query cache entry. They did not: `tiers` is
 * part of the query key (see `queryKey` below), so two different tier
 * selections are two different cache entries and two separate network
 * requests. Passing `tiers` here still works for any other caller that
 * wants server-key-scoped tier filtering, but `MyTasksPage` deliberately no
 * longer uses it.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchMyTasks, type MyTaskDto } from '../services/api/tasks';
import { useAuthOptional } from '../services/authContext';

export const MY_TASKS_BASE_KEY = ['tasks', 'mine'] as const;

export interface UseMyTasksOptions {
  /** Petra UAT 2026-05-19 — when true, bypass the terminal-status filter on the server. */
  includeDone?: boolean;
  /** Plan 50-06 (FC-01) — tier ids to filter by; empty/undefined = all tiers. */
  tiers?: number[];
}

/**
 * Pure sort + filter function.
 *
 * Sort key: `tier?.orderIndex ?? Infinity` ascending so P1 < P2 < ... < non-SLA.
 * Tie-break within same tier: `projectName.localeCompare`.
 *
 * Filter: when `tiers` is non-empty, only rows whose `tier.id` is in the set
 * are included. Tier-less tasks (tier=null/undefined) are excluded when any
 * explicit tier selection is active — they only appear with the default
 * "all tiers" empty filter.
 *
 * Exported so tests can import and call directly without rendering the hook.
 */
export function sortAndFilter(rows: MyTaskDto[], tiers: number[]): MyTaskDto[] {
  const activeTiers = tiers ?? [];
  const filtered = activeTiers.length
    ? rows.filter((t) => t.tier != null && activeTiers.includes(t.tier.id))
    : rows;

  return [...filtered].sort((a, b) => {
    const ai = a.tier?.orderIndex ?? Infinity;
    const bi = b.tier?.orderIndex ?? Infinity;
    if (ai !== bi) return ai - bi;
    return a.projectName.localeCompare(b.projectName);
  });
}

export function useMyTasks(options: UseMyTasksOptions = {}) {
  const auth = useAuthOptional();
  const effectiveUserId = auth?.impersonatedUser?.id ?? auth?.user?.id ?? null;
  const includeDone = options.includeDone ?? false;
  const tiers = options.tiers ?? [];
  return useQuery<MyTaskDto[], Error, MyTaskDto[]>({
    queryKey: [...MY_TASKS_BASE_KEY, effectiveUserId, { includeDone, tiers }],
    queryFn: () => fetchMyTasks(includeDone),
    staleTime: 30_000,
    enabled: effectiveUserId != null,
    select: (rows) => sortAndFilter(rows, tiers),
  });
}

export type { MyTaskDto };
